import * as React from 'react';
import { withStyles, StyleRulesCallback, WithStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { IObservableValue, observable, observe } from 'mobx';
import { defaultPageable, emptyPage, Page, Pageable } from '../../api/pageable';
import PaginationNavigation from '../../components/PaginationNavigation';
import { createCluster, getQuestions } from './questionCatalogueApi';
import { Spinner } from '../../spinner/Spinner';
import { translation } from '../../i18n';
import { TextField } from '@material-ui/core';
import { List } from '@material-ui/core';
import { ListItem } from '@material-ui/core';
import { Cancel as CancelIcon } from '@material-ui/icons';
import { Save as SaveIcon } from '@material-ui/icons';
import { ListItemText } from '@material-ui/core';
import { ListItemSecondaryAction } from '@material-ui/core';
import { Button } from '@material-ui/core';
import { QuestionDto } from '../questionTypes';
import { log } from '../../logConfig';
import { emptyValidationErrors, isValidationErrors, ValidationErrors } from '../../api/restApi';
import { emptyQuestionClusterRequest, QuestionClusterRequest } from '../questionClusterTypes';
import { Typography } from '@material-ui/core';
import { renderRemainingErrors } from '../../util/formUtils';
import { createPicker } from '../../util/picker';
import { isValidQuestionCluster } from '../../util/validations';
import EmptyPage from '../../components/emptyPage/EmptyPage';
import QuestionPreview from '../../components/preview/QuestionPreview';
import SaveSnackBar from '../../components/SaveSnackBar';

interface Props {
  renderSearch: (showSearch: boolean) => void;
  searchText: IObservableValue<string>;
}

type LocalStyles = 'previewArea';

const localStyles: StyleRulesCallback<LocalStyles> = theme => ({

  previewArea: {
    padding: theme.spacing.unit * 2,
  },
});

type PropsWithStyle = WithStyles<LocalStyles> & Props;

@observer
class ModuleTab extends React.Component<PropsWithStyle, {}> {

  @observable private loading: boolean = true;
  @observable private questions: Page<QuestionDto> = emptyPage();
  @observable private pageAble: Pageable = defaultPageable();
  @observable private selectedQuestions: QuestionDto[] = [];
  @observable private newCluster: QuestionClusterRequest = emptyQuestionClusterRequest();
  @observable private validationErrors: ValidationErrors = emptyValidationErrors();
  private expanded: IObservableValue<string> = observable.box('');
  private saveLoading: IObservableValue<boolean> = observable.box(false);
  private displaySnackbar: IObservableValue<boolean> = observable.box(false);

  constructor(props: PropsWithStyle) {
    super(props);
    this.props.renderSearch(true);
    this.onSaveClicked = this.onSaveClicked.bind(this);
  }

  public componentDidMount(): void {

    observe(
      this.props.searchText,
      () => {
        this.loadQuestions();
        log.info('Cluster ' + this.props.searchText.get())
      }
    );
    this.loadQuestions();
  }

  public render() {
    const errorPicker = createPicker(this.validationErrors.fieldErrors);
    return (
      <>
        {
          this.saveLoading.get() ?
            <Spinner/> :
            <>
              <div style={{width: '100%', textAlign: 'right'}}>
                <div>
                  <Button onClick={this.onSaveClicked}><SaveIcon/> {translation.t('actions.save')}</Button>
                </div>
              </div>
              <div>
                <TextField
                  label={translation.t('cluster.title')}
                  fullWidth={true}
                  value={this.newCluster.title}
                  onChange={ev => {
                    this.newCluster.title = ev.target.value
                  }}
                />
                <TextField
                  label={translation.t('cluster.description')}
                  multiline={true}
                  fullWidth={true}
                  value={this.newCluster.description}
                  onChange={ev => {
                    this.newCluster.description = ev.target.value
                  }}
                />
                <Typography variant={'subtitle1'}>{translation.t('cluster.question')}:</Typography>
                {
                  this.selectedQuestions.length === 0 ?
                    <>
                      <Typography>{translation.t('createCatalogueComponent.noQuestionsSelected')}</Typography>
                      {renderRemainingErrors(errorPicker.pick(e => e.field === 'question'))}
                    </>
                    :
                    <List>
                      {this.selectedQuestions.map((question, index) => (
                        <ListItem
                          key={question.id + '' + index}
                          button={false}
                        >
                          <ListItemText primary={this.getQuestionTitle(question)}/>
                          <ListItemSecondaryAction>
                            <Button
                              onClick={() => this.selectedQuestions.splice(this.selectedQuestions.indexOf(question), 1)}>
                              <CancelIcon/>
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                }
                {renderRemainingErrors(errorPicker.remaining())}
              </div>
              <div className={this.props.classes.previewArea}>
                {this.loading ?
                  <Spinner/> :
                  <>
                    {this.questions.content.length === 0 ?
                      <EmptyPage message={translation.t('noEntry.questionCluster.title')}
                                 searchText={this.props.searchText.get()}
                                 displayFab={false}/> :
                      <>
                        {
                          this.questions.content.map(question => {
                            return (<QuestionPreview key={question.id + 'question'}
                                                     expanded={this.expanded}
                                                     question={question}
                                                     handleAdd={this.handleAdd}/>)
                          }
                        )}
                        <PaginationNavigation
                          totalPages={this.questions.totalPages}
                          page={this.questions.pageable.pageNumber!!}
                          onChangePage={this.handleChangePage}/>
                      </>
                    }
                  </>
                }
              </div>
              <SaveSnackBar onClose={() => this.displaySnackbar.set(false)} open={this.displaySnackbar.get()}
                            massage={translation.t('clusterTab.saved')}/>
            </>
        }
      </>
    );
  }

  private onSaveClicked() {
    this.selectedQuestions.map((question, index) => {
      question.position = index + 1;
      this.newCluster.questions.push(question);
    });

    this.validationErrors = isValidQuestionCluster(this.newCluster);
    if (this.validationErrors === undefined || this.validationErrors.fieldErrors.length === 0) {
      this.saveLoading.set(true);
      createCluster(this.newCluster).then(
        data => {
          this.saveLoading.set(false);
          if (isValidationErrors(data)) {
            this.validationErrors = data;
          } else {
            this.validationErrors = emptyValidationErrors();
            this.displaySnackbar.set(true);
            this.newCluster.title = '';
            this.newCluster.description = '';
            this.newCluster.questions = [];
            this.selectedQuestions = [];
          }
        }
      )
    }
  }

  private getQuestionTitle = (question: QuestionDto): string => {
    if (question.question) {
      return question.question
    } else {
      return translation.t('createCatalogueComponent.noQuestion')
    }
  };

  private handleAdd = (question: QuestionDto) => {
    this.selectedQuestions.push(question)
  };

  private handleChangePage = (event: any, page: number) => {
    this.pageAble.page = page;
    this.loadQuestions();
  };

  private loadQuestions = () => {
    this.loading = true;
    getQuestions({searchText: this.props.searchText.get()}, this.pageAble).then(page => {
      this.questions = page;
      this.loading = false;
    });
  };

}

export default withStyles(localStyles)(ModuleTab);
